// Reads CV text into *suggestions*. Nothing produced here is a fact: the user must
// accept each candidate, and even an accepted candidate lands as "needs review".
//
// The rule that shapes every branch below: when the source is ambiguous, the parser
// leaves the field empty, keeps the printed text as evidence, and says what it could
// not read in `warnings`. A wrong employer or date on a resume costs the user more
// than a blank field does.

import type {
  CandidateFact,
  CandidatePayload,
  CandidateSection,
  ContactField,
  EntryDraft,
  Period,
} from './types';
import { blankPeriod, nextId } from './model';
import { MAX_SHORT, parseDateToken } from './validate';

export type ParseOutcome = {
  candidates: CandidateFact[];
  /** Source lines that could not be placed in any section, kept for the reviewer. */
  unclassified: string[];
  notes: string[];
};

const MAX_CANDIDATES = 120;
const MAX_SKILLS = 60;
const MAX_UNCLASSIFIED = 80;
const MAX_LINES = 4000;
const MAX_BULLETS = 12;
const MAX_SUMMARY = 1200;
/** Contact details sit at the top of a CV and sometimes at the bottom, so those two
 *  bands are scanned when no Contact heading exists. */
const HEAD_BAND = 12;
const TAIL_BAND = 4;

const MONTH_SRC =
  'Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?' +
  '|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?';

const DATE_SRC =
  `(?:${MONTH_SRC}\\.?\\s*,?\\s*(?:19|20)\\d{2}` +
  '|(?:19|20)\\d{2}[./-](?:0?[1-9]|1[0-2])' +
  '|(?:0?[1-9]|1[0-2])[./](?:19|20)\\d{2}' +
  '|(?:19|20)\\d{2}' +
  '|\\b(?:present|current|now|today)\\b)';

const SEP_SRC = '(?:[-–—~]|\\bto\\b|\\buntil\\b|\\bthrough\\b)';
const RANGE_SRC = `(${DATE_SRC})\\s*${SEP_SRC}\\s*(${DATE_SRC})`;
const EMAIL_SRC = '[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}';
const URL_SRC =
  '(?:https?://)?(?:www\\.)?[A-Za-z0-9][A-Za-z0-9-]*(?:\\.[A-Za-z0-9-]+)+(?:/[A-Za-z0-9._~:/?#@!$&\'()*+,;=%-]*)?';

// Every helper builds a fresh expression: a shared `g` flag would carry `lastIndex`
// from one call to the next and make matching depend on call order.
const rangeRe = (): RegExp => new RegExp(RANGE_SRC, 'i');
const dateRe = (): RegExp => new RegExp(DATE_SRC, 'gi');
const dateOnceRe = (): RegExp => new RegExp(DATE_SRC, 'i');
const emailAllRe = (): RegExp => new RegExp(EMAIL_SRC, 'gi');
const urlAllRe = (): RegExp => new RegExp(URL_SRC, 'gi');
const bulletRe = (): RegExp => /^[-*•‣▪◦]\s+/;
const strongSepRe = (): RegExp => new RegExp('\\s+(?:[|·•‣▪]|[-–—])\\s+|\\s+at\\s+', 'g');
const commaSepRe = (): RegExp => new RegExp('/(?!.)/|,(?!\\d)\\s*|;\\s*|\\s+\\|\\s+', 'g');
const labelRe = (): RegExp => /^([A-Za-z][A-Za-z/()& +.-]{1,30}?):\s*(.+)$/;

const ROLE_WORDS = [
  'engineer', 'developer', 'programmer', 'architect', 'consultant', 'manager',
  'lead', 'intern', 'analyst', 'designer', 'specialist', 'scientist', 'researcher',
  'administrator', 'coordinator', 'associate', 'assistant', 'representative',
  'technician', 'operator', 'officer', 'director', 'executive', 'president',
  'cto', 'ceo', 'cfo', 'coo', 'vp', 'product owner', 'scrum', 'devops', 'sre',
  'contractor', 'freelancer', 'professor', 'lecturer', 'teacher', 'tutor', 'nurse',
  'clerk', 'writer', 'editor', 'strategist', 'marketer', 'accountant', 'auditor',
  'recruiter', 'intern', 'fellow', 'trainee',
];

const ROLE_RE = new RegExp(
  `(?:\\b(?:${ROLE_WORDS.join('|')})s?\\b|\\b(?:cto|ceo|cfo|coo|vp)\\b)` +
    '|^(?:senior|junior|staff|principal|head|chief|lead)\\b',
  'i',
);

const COMPANY_MARK_SRC =
  '\\b(?:inc|llc|ltd|limited|corp|corporation|co|company|plc|gmbh|technologies|technology' +
  '|solutions|systems|software|services|labs|laboratories|group|holdings|agency|studio' +
  '|institute|university|college|school|academy|foundation|partners|associates|bank|media' +
  '|networks|ventures|industries|enterprises)\\b';
const companyRe = (): RegExp => new RegExp(COMPANY_MARK_SRC, 'i');

const CREDENTIAL_SRC =
  '\\b(?:b\\.?a\\.?|b\\.?s\\.?c?\\.?|b\\.?tech\\.?|b\\.?e\\.?|m\\.?a\\.?|m\\.?s\\.?c?\\.?' +
  '|m\\.?tech\\.?|mba|ph\\.?d\\.?|associate|bachelor(?:\\s+of\\s+(?:arts|science|engineering))?|master' +
  '(?:\\s+of\\s+(?:arts|science|business\\s+administration|engineering))?|doctorate|doctoral' +
  '|diploma|certificate|high\\s+school|secondary\\s+school|postgraduate|undergraduate)\\b';
const credentialRe = (): RegExp => new RegExp(CREDENTIAL_SRC, 'i');

const METRIC_RE =
  /\d[.,]?\d*\s*(?:%|×|x\b|k\b|mn\b|bn\b|million|billion|thousand|hours?|days?|weeks?|months?|users?|customers?|clients?|engineers?|people|records?|requests?|stores?|teams?|points?)\b/i;

const ACHIEVEMENT_VERB_RE =
  /\b(?:increased|reduced|improved|saved|grew|doubled|tripled|awarded|ranked|published|generated|won|cut)\b/i;

const GEO_TAIL_RE = /(?:^|\s)(?:city|county|state|province|district|area|prefecture)$/i;
const CITY_COUNTRY_RE = /^[A-Z][A-Za-z.''-]+,\s*[A-Z][A-Za-z.''\s-]*$/;

const SECTION_HEADERS: ReadonlyArray<{ section: CandidateSection; re: RegExp }> = [
  { section: 'contact', re: /^(?:contact|contact details|contact information|personal details|personal information)$/i },
  { section: 'summary', re: /^(?:summary|profile|professional summary|professional profile|objective|about me|about)$/i },
  { section: 'experience', re: /^(?:work |professional |employment |career )?(?:experience|history|employment)$/i },
  { section: 'education', re: /^(?:education|academic background|academic history|qualifications)$/i },
  { section: 'skills', re: /^(?:skills|technical skills|core competencies|competencies|technologies|tech(?:nical)? stack|tools|expertise|areas of expertise)$/i },
  { section: 'certifications', re: /^(?:certifications?|certificates?|licenses?|licences?|training)$/i },
  { section: 'projects', re: /^(?:projects?|personal projects?|side projects?|selected projects?|open source)$/i },
];

const LABEL_KEYS: Readonly<Record<string, CandidatePayload['kind'] | ContactField | 'skills-inline'>> = {
  email: 'email',
  'e-mail': 'email',
  phone: 'phone',
  mobile: 'phone',
  cell: 'phone',
  tel: 'phone',
  telephone: 'phone',
  location: 'location',
  address: 'location',
  city: 'location',
  name: 'fullName',
  'full name': 'fullName',
  summary: 'summary',
  profile: 'summary',
  objective: 'summary',
  about: 'summary',
  'about me': 'summary',
  skills: 'skills-inline',
  'technical skills': 'skills-inline',
  'core competencies': 'skills-inline',
  tools: 'skills-inline',
  technologies: 'skills-inline',
};

const LINK_LABELS: Readonly<Record<string, string>> = {
  linkedin: 'LinkedIn',
  github: 'GitHub',
  gitlab: 'GitLab',
  website: 'Website',
  portfolio: 'Portfolio',
  url: 'Website',
  site: 'Website',
};

type Line = { n: number; text: string };

type DateRead = {
  period: Period;
  /** Header text with every date token removed, so field parsing sees only words. */
  rest: string;
  warnings: string[];
};

type TokenRead = { value: string; current: boolean; error: string | null };

type HeaderFields = {
  organization: string;
  role: string;
  location: string;
  /** Long text that reads as a description rather than a title. */
  prose: string[];
  warnings: string[];
};

type Block = {
  header: string[];
  dated: boolean;
  bullets: string[];
  lines: Line[];
  warnings: string[];
};

type Sink = {
  push: (fact: CandidateFact) => void;
  used: Set<number>;
  unclassified: string[];
};

export function parseCvText(text: string): ParseOutcome {
  const candidates: CandidateFact[] = [];
  const unclassified: string[] = [];
  const notes: string[] = [];
  const seen = new Set<string>();
  let capped = false;

  const push = (fact: CandidateFact): void => {
    const key = `${fact.section}|${payloadSignature(fact.payload)}`;
    if (seen.has(key)) return;
    if (candidates.length >= MAX_CANDIDATES) {
      capped = true;
      return;
    }
    seen.add(key);
    candidates.push(fact);
  };

  const lines = linesOf(text);
  if (lines.length === 0) {
    return {
      candidates,
      unclassified,
      notes: ['The extracted text holds no readable lines, so there is nothing to review.'],
    };
  }

  const sink: Sink = { push, used: new Set<number>(), unclassified };
  const { sections, unowned, headings } = groupByHeading(lines);
  const lastLine = lines.length > 0 ? (lines[lines.length - 1]?.n ?? 0) : 0;

  parseLabels(lines, sink);

  for (const section of ['contact', 'summary', 'experience', 'education', 'skills', 'certifications', 'projects'] as const) {
    const owned = sections.get(section);
    if (!owned || owned.length === 0) continue;
    parseSection(section, owned, sink);
  }

  // Unowned lines near the start or end are contact-shaped often enough to look at;
  // nowhere else is position alone treated as evidence of what a line means.
  const band = unowned.filter(
    (line) => !sink.used.has(line.n) && (line.n <= HEAD_BAND || line.n > lastLine - TAIL_BAND),
  );
  parseContact(band, sink, { allowName: true, allowLocation: true });

  for (const line of unowned) {
    if (sink.used.has(line.n)) continue;
    if (unclassified.length < MAX_UNCLASSIFIED) unclassified.push(line.text);
  }

  if (headings === 0) {
    notes.push(
      'No section headings were recognized, so only labelled values and contact details near the top were proposed. Add headings to the text, or fill the profile by hand.',
    );
  }
  if (capped) {
    notes.push(`Only the first ${MAX_CANDIDATES} suggestions are listed. Shorten the text and import again for the rest.`);
  }
  if (unclassified.length > 0) {
    notes.push(
      `${unclassified.length} source line${unclassified.length === 1 ? '' : 's'} could not be placed in a section. Nothing was invented for them; add anything that matters by hand.`,
    );
  }
  if (candidates.length === 0) {
    notes.push('No facts could be read from this text. Edit the profile directly instead of importing.');
  }

  return { candidates, unclassified, notes };
}

function linesOf(text: string): Line[] {
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((raw, index): Line => ({ n: index + 1, text: squeeze(raw) }))
    .filter((line) => line.text.length > 0 && !/^[-=_*•\s]{3,}$/.test(line.text))
    .slice(0, MAX_LINES);
}

function groupByHeading(lines: Line[]): {
  sections: Map<CandidateSection, Line[]>;
  unowned: Line[];
  headings: number;
} {
  const sections = new Map<CandidateSection, Line[]>();
  const unowned: Line[] = [];
  let current: CandidateSection | null = null;
  let headings = 0;

  for (const line of lines) {
    const found = headerSection(line.text);
    if (found !== null) {
      headings += 1;
      current = found;
      if (!sections.has(found)) sections.set(found, []);
      continue;
    }
    if (current === null) {
      unowned.push(line);
      continue;
    }
    (sections.get(current) ?? unowned).push(line);
  }

  return { sections, unowned, headings };
}

/** A heading is a short standalone word, never a line that carries content. */
function headerSection(text: string): CandidateSection | null {
  const bare = text.replace(/[:|]+\s*$/, '').trim();
  if (bare.length === 0 || bare.length > 40) return null;
  if (/[\d@]/.test(bare)) return null;
  for (const entry of SECTION_HEADERS) {
    if (entry.re.test(bare)) return entry.section;
  }
  return null;
}

function parseSection(section: CandidateSection, lines: Line[], sink: Sink): void {
  const free = lines.filter((line) => !sink.used.has(line.n));
  switch (section) {
    case 'contact':
      parseContact(free, sink, { allowName: true, allowLocation: true });
      return;
    case 'summary':
      parseSummary(free, sink);
      return;
    case 'experience':
      parseEntries('experience', free, sink, blockToExperience);
      return;
    case 'education':
      parseEntries('education', free, sink, blockToEducation);
      return;
    case 'projects':
      parseEntries('projects', free, sink, blockToProject);
      return;
    case 'skills':
      parseSkills(free, sink);
      return;
    case 'certifications':
      parseCertifications(free, sink);
      return;
  }
}

function parseEntries<K extends 'experience' | 'education' | 'projects'>(
  section: K,
  lines: Line[],
  sink: Sink,
  toEntry: (block: Block) => { draft: EntryDraft<K>; warnings: string[] } | null,
): void {
  for (const block of splitBlocks(lines)) {
    const parsed = toEntry(block);
    if (!parsed) {
      for (const line of block.lines) {
        if (!sink.used.has(line.n) && sink.unclassified.length < MAX_UNCLASSIFIED) {
          sink.unclassified.push(line.text);
        }
      }
      continue;
    }
    const payloadKind: CandidatePayload['kind'] =
      section === 'experience' ? 'experience' : section === 'education' ? 'education' : 'project';
    const draft = parsed.draft;
    sink.push(
      candidate(
        section,
        titleForEntry(payloadKind, draft),
        { kind: payloadKind, entry: draft } as CandidatePayload,
        block,
        [...block.warnings, ...parsed.warnings],
        sink,
      ),
    );
  }
}

function splitBlocks(lines: Line[]): Block[] {
  const blocks: Block[] = [];
  let header: string[] = [];
  let bullets: string[] = [];
  let owned: Line[] = [];
  let warnings: string[] = [];
  let dated = false;
  let open = false;
  let previous = '';

  const close = (): void => {
    if (!open) return;
    blocks.push({ header, dated, bullets, lines: owned, warnings });
    header = [];
    bullets = [];
    owned = [];
    warnings = [];
    dated = false;
    previous = '';
    open = false;
  };

  for (const line of lines) {
      const isBullet = bulletRe().test(line.text);
    const text = isBullet ? squeeze(line.text.replace(bulletRe(), '')) : line.text;
    if (text.length === 0) continue;
    const hasDate = dateOnceRe().test(line.text);

    if (!open) open = true;
    else if (bullets.length > 0 && !isBullet && !isContinuation(previous, text)) {
      close();
      open = true;
    } else if (hasDate && dated && bullets.length === 0) {
      close();
      open = true;
    }

    if (isBullet && header.length === 0 && bullets.length === 0 && hasDate) {
      // A bulleted entry line: the marker is formatting, not meaning.
      header.push(text);
      dated = true;
      warnings.push('This entry is a bullet in the source, so its grouping with other lines may be wrong.');
    } else if (isBullet) {
      const last = bullets[bullets.length - 1];
      if (last !== undefined && isContinuation(last, text)) bullets[bullets.length - 1] = squeeze(`${last} ${text}`);
      else bullets.push(text);
    } else if (bullets.length > 0 && isContinuation(previous, text)) {
      const last = bullets[bullets.length - 1];
      if (last !== undefined) bullets[bullets.length - 1] = squeeze(`${last} ${text}`);
      else bullets.push(text);
    } else {
      header.push(text);
      if (hasDate) dated = true;
    }

    owned.push(line);
    previous = text;
  }
  close();
  return blocks;
}

function blockToExperience(block: Block): { draft: EntryDraft<'experience'>; warnings: string[] } | null {
  const warnings: string[] = [];
  const headerText = squeeze(block.header.join(' '));
  if (headerText.length === 0) return null;
  const dates = readDates(headerText);
  warnings.push(...dates.warnings);
  const fields = classifyHeader(splitPieces(dates.rest));
  warnings.push(...fields.warnings);

  const responsibilities: string[] = [];
  const achievements: string[] = [];
  for (const bullet of block.bullets) {
    if (METRIC_RE.test(bullet) || ACHIEVEMENT_VERB_RE.test(bullet)) achievements.push(bullet);
    else responsibilities.push(bullet);
  }
  if (fields.prose.length > 0) {
    responsibilities.push(...fields.prose);
    warnings.push('Some text on this line reads as a description rather than a title, so it was placed under responsibilities.');
  }
  if (responsibilities.length + achievements.length > MAX_BULLETS) {
    warnings.push(`Only the first ${MAX_BULLETS} description lines were kept; the rest are still visible in the source text.`);
  }
  if (fields.organization.length === 0 && fields.role.length === 0) {
    return null;
  }
  if (!fields.role) warnings.push('No role title was read on this line. Add it only if the CV states one.');

  return {
    draft: {
      organization: truncate(fields.organization, MAX_SHORT),
      role: truncate(fields.role, MAX_SHORT),
      location: truncate(fields.location, MAX_SHORT),
      period: dates.period,
      responsibilities: responsibilities.slice(0, MAX_BULLETS),
      achievements: achievements.slice(0, MAX_BULLETS),
    },
    warnings,
  };
}

function blockToEducation(block: Block): { draft: EntryDraft<'education'>; warnings: string[] } | null {
  const warnings: string[] = [];
  const headerText = squeeze(block.header.join(' ') + (block.bullets.length > 0 ? ' ' + block.bullets.join(' ') : ''));
  if (headerText.length === 0) return null;
  const dates = readDates(headerText);
  warnings.push(...dates.warnings);
  const pieces = splitPieces(dates.rest).filter((piece) => !isSourceNoise(piece));

  let credential = '';
  let area = '';
  let institution = '';
  const leftovers: string[] = [];
  for (const piece of pieces) {
    const match = credentialRe().exec(piece);
    if (match && credential.length === 0) {
      credential = squeeze(match[0] ?? '');
      const tail = squeeze(piece.replace(credentialRe(), ' '));
      const fieldMatch = tail.match(/\b(?:in|of|field)\s+(.+)$/i);
      if (fieldMatch?.[1]) {
        area = truncate(squeeze(fieldMatch[1]), MAX_SHORT);
        const remainder = squeeze(tail.replace(fieldMatch[0], ' '));
        if (remainder.length > 0) leftovers.push(remainder);
      } else if (tail.length > 0) {
        leftovers.push(tail);
      }
      const head = squeeze(piece.slice(0, match.index ?? 0));
      if (head.length > 0) leftovers.push(head);
      continue;
    }
    leftovers.push(piece);
  }
  institution = truncate(leftovers.find((piece) => !looksLikeLocation(piece)) ?? '', MAX_SHORT);
  if (institution.length > 0) {
    const index = leftovers.indexOf(institution);
    if (index >= 0) leftovers.splice(index, 1);
  }

  if (institution.length === 0 && credential.length === 0) return null;
  if (institution.length === 0) {
    warnings.push('The school or university was not clearly separable from the qualification on this line.');
  }
  if (credential.length === 0) {
    warnings.push('No degree or qualification word was found. Add it only if the CV states one.');
  }
  for (const extra of leftovers) {
    if (extra !== area) warnings.push(`"${extra}" was not placed in a field on this entry.`);
  }
  if (dates.period.raw.length === 0) {
    warnings.push('No dates were read on this entry. NextRole did not guess them.');
  }

  return {
    draft: {
      institution,
      credential: truncate(credential, MAX_SHORT),
      area,
      period: dates.period,
    },
    warnings,
  };
}

function blockToProject(block: Block): { draft: EntryDraft<'projects'>; warnings: string[] } | null {
  const warnings: string[] = [];
  let headerText = squeeze(block.header.join(' '));
  if (headerText.length === 0 && block.bullets.length > 0) {
    headerText = squeeze(block.bullets.shift() ?? '');
    warnings.push('This project was read from a bullet line, so its title and description may need separating.');
  }
  if (headerText.length === 0) return null;

  const withoutEmails = headerText.replace(emailAllRe(), ' ');
  const url = urlAllRe().exec(withoutEmails)?.[0] ?? '';
  if (url.length > 0) headerText = squeeze(withoutEmails.replace(url, ' '));

  const dates = readDates(headerText);
  warnings.push(...dates.warnings);
  const pieces = splitPieces(dates.rest).filter((piece) => !isSourceNoise(piece));
  const name = truncate(pieces.find((piece) => piece.replace(/\W/g, '').length > 1) ?? '', MAX_SHORT);
  if (name.length === 0) return null;
  for (const extra of pieces.filter((piece) => piece !== name)) {
    warnings.push(`"${extra}" was not placed in a field on this project.`);
  }
  const description = truncate(block.bullets.join(' '), 600);

  return {
    draft: { name, url: truncate(url, MAX_SHORT), description, period: dates.period },
    warnings,
  };
}

function parseSkills(lines: Line[], sink: Sink): void {
  let count = 0;
  for (const line of lines) {
    const text = squeeze(line.text.replace(bulletRe(), ''));
    const pieces = splitSkills(text);
    if (pieces.length === 0) {
      if (sink.unclassified.length < MAX_UNCLASSIFIED) sink.unclassified.push(line.text);
      continue;
    }
    sink.used.add(line.n);
    for (const piece of pieces) {
      count += 1;
      if (count > MAX_SKILLS) {
        sink.unclassified.push(`More than ${MAX_SKILLS} skills were found; "${piece}" and the rest were not proposed.`);
        return;
      }
      sink.push(
        candidate(
          'skills',
          `Skill: ${truncate(piece, 40)}`,
          { kind: 'skill', entry: { name: piece } },
          { lines: [line], warnings: [] },
          lineIsGuess(text, pieces.length) ? [`Skills were split from the line "${squeeze(line.text)}".`] : [],
          sink,
        ),
      );
    }
  }
}

const SKILL_SEP_RE = (): RegExp => new RegExp(',(?!\\d)|;|\\s+\\|\\s+|\\s+•\\s+|\\s+·\\s+', 'g');

function splitSkills(text: string): string[] {
  const stripped = text.replace(/^[A-Za-z][A-Za-z /()-]{1,30}:\s*/, '');
  const parts = stripped
    .split(SKILL_SEP_RE())
    .map((piece) => squeeze(piece.replace(/^[-*•\s]+/, '')))
    .filter((piece) => piece.length > 0);
  const out: string[] = [];
  for (const piece of parts) {
    if (piece.length > MAX_SHORT) continue;
    if (countWords(piece) > 8) continue;
    if (out.includes(piece.toLowerCase())) continue;
    out.push(piece);
  }
  return out;
}

function parseCertifications(lines: Line[], sink: Sink): void {
  for (const line of lines) {
    const text = squeeze(line.text.replace(bulletRe(), ''));
    if (text.length === 0) continue;
    const dates = readDates(text);
    const pieces = splitPieces(dates.rest).filter((piece) => !isSourceNoise(piece));
    const name = pieces.shift() ?? '';
    if (name.replace(/\W/g, '').length < 2) {
      if (sink.unclassified.length < MAX_UNCLASSIFIED) sink.unclassified.push(line.text);
      continue;
    }
    const issuerPiece = pieces.shift() ?? '';
    const issuer = truncate(squeeze(issuerPiece.replace(/^(?:from|by|issued(?:\s+by)?|provider)\b[:\s]*/i, '')), MAX_SHORT);
    const warnings = [...dates.warnings];
    if (issuer.length === 0) warnings.push('The issuing body was not read from this line.');
    if (dates.period.raw.length === 0) warnings.push('No date was read for this certification.');
    for (const extra of pieces) warnings.push(`"${extra}" was not placed in a field on this certification.`);
    sink.push(
      candidate(
        'certifications',
        `Certification: ${truncate(name, 40)}`,
        { kind: 'certification', entry: { name: truncate(name, MAX_SHORT), issuer, date: dates.period.raw } },
        { lines: [line], warnings: [] },
        warnings,
        sink,
      ),
    );
  }
}

function parseSummary(lines: Line[], sink: Sink): void {
  const kept: Line[] = [];
  const value = squeeze(
    lines
      .filter((line) => !sink.used.has(line.n))
      .map((line) => squeeze(line.text.replace(bulletRe(), '')))
      .join(' '),
  );
  if (value.length < 20) {
    for (const line of lines) {
      if (sink.used.has(line.n)) continue;
      if (sink.unclassified.length < MAX_UNCLASSIFIED) sink.unclassified.push(line.text);
    }
    return;
  }
  const warnings: string[] = [];
  const truncated = value.length > MAX_SUMMARY;
  for (let index = 0; index < Math.min(lines.length, 6); index += 1) {
    const line = lines[index];
    if (line) kept.push(line);
  }
  if (truncated) warnings.push(`The summary was shortened to ${MAX_SUMMARY} characters for a resume.`);
  sink.push(
    candidate('summary', 'Professional summary', { kind: 'summary', value: truncate(value, MAX_SUMMARY) },
      { lines: kept, warnings }, warnings, sink),
  );
}

function parseContact(lines: Line[], sink: Sink, allow: { allowName: boolean; allowLocation: boolean }): void {
  let nameProposed = false;
  for (const line of lines) {
    if (sink.used.has(line.n)) continue;
    const text = line.text;
    let matched = false;

    for (const email of matchAll(text, emailAllRe())) {
      matched = true;
      sink.push(
        candidate('contact', 'Email', { kind: 'contact-field', field: 'email', value: email },
          { lines: [line], warnings: [] }, [], sink),
      );
    }

    const withoutEmails = text.replace(emailAllRe(), ' ');
    for (const url of matchAll(withoutEmails, urlAllRe())) {
      if (url.replace(/\W/g, '').length < 3) continue;
      matched = true;
      sink.push(
        candidate('contact', `${labelForUrl(url)} link`, { kind: 'contact-link', label: labelForUrl(url), url },
          { lines: [line], warnings: [] },
          [/^[a-z]/.test(url) ? 'The address has no protocol, so it was read from plain text. Check it opens correctly.' : ''],
          sink),
      );
    }

    const labelledLocation = text.match(/^(?:located in|based in)\s+(.+)$/i)?.[1];
    if (labelledLocation) {
      matched = true;
      sink.push(contactFieldCandidate('location', squeeze(labelledLocation), line, []));
      sink.used.add(line.n);
    }

    if (phoneLike(text)) {
      matched = true;
      sink.push(
        candidate('contact', 'Phone', { kind: 'contact-field', field: 'phone', value: text },
          { lines: [line], warnings: [] },
          ['The number was read from a line with nothing but digits, so its country code may be missing.'],
          sink),
      );
    }

    if (allow.allowName && !nameProposed && !matched && looksLikePersonName(text)) {
      nameProposed = true;
      matched = true;
      sink.push(
        candidate('contact', 'Name', { kind: 'contact-field', field: 'fullName', value: text },
          { lines: [line], warnings: [] },
          ['Read as the name because it is a short line near the top of the document. Check it.'],
          sink),
      );
    }

    if (allow.allowLocation && !matched && CITY_COUNTRY_RE.test(text) && !GEO_TAIL_RE.test(text) === false) {
      matched = true;
      sink.push(
        candidate('contact', 'Location', { kind: 'contact-field', field: 'location', value: text },
          { lines: [line], warnings: [] },
          ['Read as a location because the line is written as "City, Region". Check it.'],
          sink),
      );
    }

    if (matched) sink.used.add(line.n);
  }
}

function contactFieldCandidate(
  field: ContactField,
  value: string,
  line: Line,
  warnings: string[],
): CandidateFact {
  return candidate(
    'contact',
    field === 'fullName' ? 'Name' : field === 'email' ? 'Email' : field === 'phone' ? 'Phone' : 'Location',
    { kind: 'contact-field', field, value } satisfies CandidatePayload,
    { lines: [line], warnings },
    warnings,
    { push: () => undefined, used: new Set<number>(), unclassified: [] },
  );
}

/** Reads `Key: value` lines anywhere in the document, since those are unambiguous. */
function parseLabels(lines: Line[], sink: Sink): void {
  for (const line of lines) {
    const match = labelRe().exec(line.text);
    const key = match?.[1];
    const value = match?.[2];
    if (!match || !key || !value) continue;
    const normalized = squeeze(key.toLowerCase());

    const linkLabel = LINK_LABELS[normalized];
    if (linkLabel) {
      const url = value.replace(/^(?:https?:\/\/)?(?:www\.)?/i, (prefix) =>
        /^https?:\/\//i.test(prefix) ? prefix : 'https://',
      );
      sink.push(
        candidate('contact', `${linkLabel} link`, { kind: 'contact-link', label: linkLabel, url },
          { lines: [line], warnings: [] }, [], sink),
      );
      sink.used.add(line.n);
      continue;
    }

    const mapped = LABEL_KEYS[normalized];
    if (mapped === undefined) continue;

    if (mapped === 'summary') {
      sink.push(
        candidate('summary', 'Professional summary', { kind: 'summary', value: truncate(squeeze(value), MAX_SUMMARY) },
          { lines: [line], warnings: [] }, [], sink),
      );
      sink.used.add(line.n);
      continue;
    }

    if (mapped === 'skills-inline') {
      for (const piece of splitSkills(value)) {
        sink.push(
          candidate('skills', `Skill: ${truncate(piece, 40)}`, { kind: 'skill', entry: { name: piece } },
            { lines: [line], warnings: [] }, [], sink),
        );
      }
      sink.used.add(line.n);
      continue;
    }

    const field = mapped as ContactField;
    const parts = field === 'phone' || field === 'fullName' || field === 'location'
      ? [squeeze(value.split(/[,;]|\s+\|\s+/)[0] ?? '')]
      : [squeeze(value.split(/[,;]|\s+\|\s+/)[0] ?? '')];
    const text = parts[0] ?? '';
    if (text.length === 0) continue;
    sink.push(
      candidate('contact', field === 'fullName' ? 'Name' : field === 'email' ? 'Email' : field === 'phone' ? 'Phone' : 'Location',
        { kind: 'contact-field', field, value: text },
        { lines: [line], warnings: [] }, [], sink),
    );
    sink.used.add(line.n);
  }
}

function readDates(text: string): DateRead {
  const warnings: string[] = [];
  const period = blankPeriod();
  let rest = text;

  const range = rangeRe().exec(text);
  if (range) {
    const printed = squeeze(range[0] ?? '');
    const startRaw = squeeze(range[1] ?? '');
    const endRaw = squeeze(range[2] ?? '');
    const start = readToken(startRaw);
    const end = readToken(endRaw);
    period.raw = printed;
    period.start = start.current ? startRaw : start.value;
    period.end = end.current ? '' : end.value;
    period.current = end.current;
    if (start.error) warnings.push(start.error);
    if (end.error) warnings.push(end.error);
    if (start.current) {
      warnings.push(`"${startRaw}" appears where a start date belongs. Check the order of these dates.`);
    }
    rest = squeeze(rest.replace(printed, ' '));
  }

  const singles = matchAll(rest, dateRe());
  if (singles.length === 1 && period.raw.length === 0) {
    const token = singles[0] ?? '';
    const read = readToken(token);
    period.raw = token;
    if (read.current) {
      warnings.push(`"${token}" is the only date on this line, so NextRole could not tell what it started. Add the dates.`);
    } else {
      warnings.push(`Only one date appears ("${token}"). NextRole did not guess whether it is a start or an end.`);
    }
    return { period, rest: squeeze(rest.replace(new RegExp(DATE_SRC, 'gi'), ' ')), warnings };
  }

  if (singles.length >= 2) {
    if (period.raw.length === 0) period.raw = singles.join(' ');
    if (singles.length === 2) {
      const first = readToken(singles[0] ?? '');
      const second = readToken(singles[1] ?? '');
      period.start = first.value;
      period.end = second.current ? '' : second.value;
      period.current = second.current;
      warnings.push(`Two dates appear without a range mark ("${singles[0]}" then "${singles[1]}"). Check which one is the start.`);
      if (first.error) warnings.push(first.error);
      if (second.error) warnings.push(second.error);
    } else {
      warnings.push(`${singles.length} dates appear on one line ("${singles.join(', ')}"). NextRole did not guess which pair belongs here.`);
    }
    return { period, rest: squeeze(rest.replace(new RegExp(DATE_SRC, 'gi'), ' ')), warnings };
  }

  if (singles.length === 1 && period.raw.length > 0) {
    warnings.push(`An extra date ("${singles[0]}") was left out of this entry.`);
    rest = squeeze(rest.replace(new RegExp(DATE_SRC, 'gi'), ' '));
  }

  return { period, rest: squeeze(rest), warnings };
}

function readToken(raw: string): TokenRead {
  const token = parseDateToken(raw);
  if (token.ok) return { value: token.value, current: token.current, error: null };
  return { value: raw, current: false, error: token.error };
}

function splitPieces(line: string): string[] {
  const strong = line.split(strongSepRe()).map((chunk) => squeeze(chunk)).filter((chunk) => chunk.length > 0);
  const out: string[] = [];
  for (const chunk of strong) {
    if (/[,;|]/.test(chunk) && countWords(chunk) >= 2) {
      for (const part of chunk.split(commaSepRe()).map((piece) => squeeze(piece.replace(/^[-–—·•*]+\s*/, '')))) {
        if (part.length > 0) out.push(part);
      }
      continue;
    }
    out.push(chunk);
  }
  return out.filter((piece) => piece.replace(/\W/g, '').length > 1);
}

function classifyHeader(pieces: string[]): HeaderFields {
  const warnings: string[] = [];
  const titles: string[] = [];
  const prose: string[] = [];
  for (const piece of pieces) {
    if (countWords(piece) > 8 || piece.length > 70) prose.push(piece);
    else titles.push(piece);
  }

  const used = new Set<number>();
  let role = '';
  let location = '';
  let organization = '';

  const roleIndex = titles.findIndex((piece) => ROLE_RE.test(piece));
  if (roleIndex >= 0) {
    role = titles[roleIndex] ?? '';
    used.add(roleIndex);
  }

  const geoIndex = titles.findIndex((piece) => looksLikeLocation(piece));
  if (geoIndex >= 0) {
    location = titles[geoIndex] ?? '';
    used.add(geoIndex);
    const neighbour = titles[geoIndex + 1];
    if (
      neighbour !== undefined &&
      !used.has(geoIndex + 1) &&
      countWords(neighbour) === 1 &&
      /^[A-Z]/.test(neighbour) &&
      !ROLE_RE.test(neighbour) &&
      !companyRe().test(neighbour)
    ) {
      location = `${location}, ${neighbour}`;
      used.add(geoIndex + 1);
    }
  }

  const orgIndex = firstUnused(titles, used, (piece) => companyRe().test(piece));
  const chosen = orgIndex >= 0 ? orgIndex : firstUnused(titles, used);
  if (chosen >= 0) {
    organization = titles[chosen] ?? '';
    used.add(chosen);
  }

  if (location.length === 0 && titles.length >= 3) {
    const last = firstUnusedFrom(titles, used, titles.length - 1);
    if (last >= 0) {
      location = titles[last] ?? '';
      used.add(last);
      warnings.push(`NextRole read "${location}" as the location because it came last. Check it.`);
    }
  }

  if (role.length === 0) {
    warnings.push(
      titles.length >= 2
        ? 'NextRole could not tell which part is the employer and which is the role title. Check both fields before confirming.'
        : 'No role title was found on this line.',
    );
  }

  const extras = titles.filter((_, index) => !used.has(index));
  for (const extra of extras) warnings.push(`"${extra}" was not placed in a field on this entry.`);

  return { organization, role, location, prose, warnings };
}

function firstUnused(titles: string[], used: Set<number>, accept?: (piece: string) => boolean): number {
  for (let index = 0; index < titles.length; index += 1) {
    const piece = titles[index];
    if (piece === undefined || used.has(index)) continue;
    if (accept ? accept(piece) : !looksLikeLocation(piece)) return index;
  }
  if (!accept) {
    for (let index = 0; index < titles.length; index += 1) {
      if (!used.has(index)) return index;
    }
  }
  return -1;
}

function firstUnusedFrom(titles: string[], used: Set<number>, from: number): number {
  for (let index = from; index >= 0; index -= 1) {
    const piece = titles[index];
    if (piece === undefined || used.has(index)) continue;
    if (!ROLE_RE.test(piece) && !companyRe().test(piece)) return index;
  }
  return -1;
}

function looksLikeLocation(piece: string): boolean {
  const text = piece.trim();
  if (text.length === 0 || text.length > 60) return false;
  if (GEO_TAIL_RE.test(text)) return true;
  if (CITY_COUNTRY_RE.test(text)) return true;
  return /^(?:remote|hybrid|onsite|on-site|relocated)$/i.test(text);
}

function looksLikePersonName(text: string): boolean {
  if (text.length > 60 || countWords(text) < 2 || countWords(text) > 5) return false;
  if (/[\d@/|]/.test(text)) return false;
  if (ROLE_RE.test(text) || companyRe().test(text)) return false;
  if (/[.,;:]/.test(text) && !/^[A-Z][a-z]+(\s+[A-Z][a-z]+)+[.]?$/.test(text)) return false;
  return text.split(/\s+/).every((word) => /^[A-Z][a-z']+$/.test(word) || /^(?:van|von|de|del|della|der|den|la|le|di|da|bin|binte|al)$/i.test(word));
}

function phoneLike(text: string): boolean {
  if (/[A-Za-z@]/.test(text)) return false;
  if (!/^\+?[\d][\d\s().-]{5,19}$/.test(text.trim())) return false;
  const digits = text.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

function isSourceNoise(piece: string): boolean {
  return /^(?:and|or|the|a|an|of|in|at|with|for|to|-|–|—|:|,)\.?$/i.test(piece.trim());
}

function labelForUrl(url: string): string {
  const host = url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0] ?? url;
  const known: ReadonlyArray<[RegExp, string]> = [
    [/linkedin\./i, 'LinkedIn'],
    [/github\./i, 'GitHub'],
    [/gitlab\./i, 'GitLab'],
    [/dribbble\./i, 'Dribbble'],
    [/behance\./i, 'Behance'],
    [/medium\./i, 'Medium'],
    [/stackoverflow\./i, 'Stack Overflow'],
    [/x\.com|twitter\./i, 'X'],
  ];
  for (const [pattern, label] of known) {
    if (pattern.test(host)) return label;
  }
  return host.split('.')[0] ?? 'Website';
}

function isContinuation(previous: string, text: string): boolean {
  if (previous.length === 0) return false;
  if (/^[a-z]/.test(text)) return true;
  if (/^(?:and|or|with|for|to|of|in|on|the|a|an|as|by|which|that)\b/i.test(text)) return true;
  return !/[.!?;:]$/.test(previous);
}

function candidate(
  section: CandidateSection,
  title: string,
  payload: CandidatePayload,
  source: { lines: Line[]; warnings: string[] },
  warnings: string[],
  sink: Sink,
): CandidateFact {
  for (const line of source.lines) sink.used.add(line.n);
  const merged = Array.from(new Set([...source.warnings, ...warnings].filter((text) => text.length > 0)));
  return {
    id: nextId('cand'),
    section,
    title,
    payload,
    evidence: source.lines.map((line) => line.text).join('\n'),
    lines: source.lines.map((line) => line.n),
    warnings: merged,
  };
}

function titleForEntry(kind: CandidatePayload['kind'], draft: EntryDraft<'experience'> | EntryDraft<'education'> | EntryDraft<'projects'>): string {
  if (kind === 'experience') {
    const e = draft as EntryDraft<'experience'>;
    return squeeze(`${e.role} ${e.organization}`).slice(0, 80) || 'Work experience entry';
  }
  if (kind === 'education') {
    const e = draft as EntryDraft<'education'>;
    return squeeze(`${e.credential} ${e.institution}`).slice(0, 80) || 'Education entry';
  }
  const p = draft as EntryDraft<'projects'>;
  return p.name.length > 0 ? `Project: ${p.name.slice(0, 60)}` : 'Project entry';
}

function matchAll(text: string, re: RegExp): string[] {
  return Array.from(text.matchAll(re), (match) => squeeze(match[0] ?? ''));
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter((word) => word.length > 0).length;
}

function lineIsGuess(text: string, pieces: number): boolean {
  return pieces > 1 && /,|;|\|/.test(text);
}

function truncate(value: string, max: number): string {
  const text = value.trim();
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}

function squeeze(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function payloadSignature(payload: CandidatePayload): string {
  switch (payload.kind) {
    case 'contact-field':
      return `contact-field:${payload.field}:${payload.value.toLowerCase()}`;
    case 'contact-link':
      return `contact-link:${payload.url.toLowerCase()}`;
    case 'summary':
      return `summary:${payload.value.slice(0, 48).toLowerCase()}`;
    case 'experience':
      return `experience:${lower(payload.entry.organization)}|${lower(payload.entry.role)}`;
    case 'education':
      return `education:${lower(payload.entry.institution)}|${lower(payload.entry.credential)}`;
    case 'skill':
      return `skill:${lower(payload.entry.name)}`;
    case 'certification':
      return `certification:${lower(payload.entry.name)}`;
    case 'project':
      return `project:${lower(payload.entry.name)}`;
  }
}

function lower(value: string): string {
  return value.trim().toLowerCase();
}
